import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import { Arrears, PagedArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';
import {
  LoanNumber,
  ValidLoanNumber
} from 'Feature/Arrears/components/ArrearsInquiryPage/core/LoanNumber';
import { Ordering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Ordering';
import { Paging } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Paging';
import ArrearsTable from 'Feature/Arrears/components/ArrearsInquiryPage/ui/ArrearsTable';
import Body from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Body';
import Header from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Header';
import CommonInformationSection from 'Feature/Arrears/components/ArrearsInquiryPage/ui/commonInformationSection/CommonInformationSection';
import { Trans, useTranslation } from 'react-i18next';

interface SearchResultSectionProps {
  loanNumber: LoanNumber;
  arrears?: PagedArrears;
  latestArrears?: Arrears;
  error?: string;
  reloadingArrears: boolean;
  clearSearchResults: () => void;
  changePaging: (paging: Paging) => void;
  changeOrdering: (ordering: Ordering) => void;
  onArrearsUpdated: (updatedArrears: Arrears) => void;
  onArrearsDeleted: () => void;
}

const SearchResultSection = ({
  loanNumber,
  arrears,
  latestArrears,
  error,
  reloadingArrears,
  clearSearchResults,
  changePaging,
  changeOrdering,
  onArrearsUpdated,
  onArrearsDeleted
}: SearchResultSectionProps) => {
  if (error) {
    return <SearchResultError clearSearchResults={clearSearchResults} />;
  }

  if (loanNumber.status === 'empty') {
    return <SearchResultEmpty />;
  }

  if (loanNumber.status === 'invalid') {
    return <SearchResultInvalid clearSearchResults={clearSearchResults} />;
  }

  if (arrears) {
    return (
      <>
        <SearchResultWithArrears loanNumber={loanNumber} />
        {latestArrears && <CommonInformationSection arrears={latestArrears} />}
        <ArrearsTable
          arrears={arrears}
          reloadingArrears={reloadingArrears}
          changePaging={changePaging}
          changeOrdering={changeOrdering}
          onArrearsUpdated={onArrearsUpdated}
          onArrearsDeleted={onArrearsDeleted}
        />
      </>
    );
  }

  return <></>;
};

const SearchResultError = ({ clearSearchResults }: { clearSearchResults: () => void }) => {
  const { t } = useTranslation('HbtArrearsInquiryPage');

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Header size="medium" color={'#DA291C'} display={'flex'} alignItems={'center'} gap={'10px'}>
        <CmhcIcon type={'icon_error_outlined'} size={CmhcIconSize.MEDIUM} />
        {t('errorSection.title')}
      </Header>
      <Body size="body">{t('errorSection.message')}</Body>
      <div>
        <Body size="link" onClick={clearSearchResults} aria-label={t('errorSection.tryAgain')}>
          {t('errorSection.tryAgain')}
        </Body>
      </div>
    </Box>
  );
};

const SearchResultEmpty = () => <></>;

const SearchResultInvalid = ({ clearSearchResults }: { clearSearchResults: () => void }) => {
  const { t } = useTranslation('HbtArrearsInquiryPage');

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <div>
        <Body size="body">{t('invalidLoanNumberSection.message')}</Body>
        <Body size="body">{`• ${t('invalidLoanNumberSection.constraint1')}`}</Body>
        <Body size="body">{`• ${t('invalidLoanNumberSection.constraint2')}`}</Body>
        <Body size="body">{`• ${t('invalidLoanNumberSection.constraint3')}`}</Body>
      </div>
      <div>
        <Body
          size="link"
          onClick={clearSearchResults}
          aria-label={t('invalidLoanNumberSection.tryAgain')}
        >
          {t('invalidLoanNumberSection.tryAgain')}
        </Body>
      </div>
    </Box>
  );
};

const SearchResultWithArrears = ({ loanNumber }: { loanNumber: ValidLoanNumber }) => {
  const { t } = useTranslation('HbtArrearsInquiryPage');
  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Body size="body">
        <Trans
          t={t}
          i18nKey="searchResultSection.message"
          values={{ loanNumber: loanNumber.value }}
          components={{ strong: <strong /> }}
        />
      </Body>
    </Box>
  );
};

export default SearchResultSection;
